import { useEffect, useState } from 'react';
import { formatServerClock, useDisplayZone } from '@adiox/ui';
import { SEVERITY_MARK, type Severity } from './describe';
import { notifications, useNotifications } from './notificationStore';

const FILTERS: { severity: Severity; label: string }[] = [
  { severity: 'danger', label: 'Alerts' },
  { severity: 'warning', label: 'Warnings' },
  { severity: 'success', label: 'Trades' },
  { severity: 'info', label: 'Info' },
];

/** The log of everything the terminal told the user this session (kept across reloads of the tab), newest first. */
export function NotificationsPanel() {
  const all = useNotifications((s) => s.items);
  const [hidden, setHidden] = useState<Severity[]>([]);
  const items = hidden.length === 0 ? all : all.filter((n) => !hidden.includes(n.severity));
  const unread = useNotifications((s) => s.unread);
  const zone = useDisplayZone();

  useEffect(() => {
    if (unread > 0) notifications.getState().markRead(); // showing the log is reading it
  }, [unread]);

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Notification log">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <span className="text-sm text-muted" data-testid="notification-count">
          {items.length === all.length ? `${all.length}` : `${items.length} of ${all.length}`} notification
          {all.length === 1 ? '' : 's'} this session
        </span>
        <div role="group" aria-label="Show" className="flex flex-1 flex-wrap gap-1">
          {FILTERS.map((f) => {
            const shown = !hidden.includes(f.severity);
            return (
              <button
                key={f.severity}
                type="button"
                aria-pressed={shown}
                onClick={() =>
                  setHidden(shown ? [...hidden, f.severity] : hidden.filter((s) => s !== f.severity))
                }
                className={`rounded border px-2 py-0.5 text-xs ${
                  shown ? 'border-accent text-fg' : 'border-line text-muted line-through'
                }`}
              >
                <span aria-hidden="true">{SEVERITY_MARK[f.severity].icon} </span>
                {f.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="rounded border border-line px-2 py-0.5 text-xs hover:border-accent disabled:opacity-40"
          disabled={items.length === 0}
          onClick={() => notifications.getState().clear()}
        >
          Clear
        </button>
      </div>
      {items.length === 0 ? (
        <p className="p-4 text-sm text-muted">
          {all.length === 0
            ? 'Nothing yet. Fills, stop levels, margin warnings and connection changes show up here.'
            : 'Nothing of the kinds chosen above.'}
        </p>
      ) : (
        <ul
          className="min-h-0 flex-1 divide-y divide-line/40 overflow-auto"
          data-testid="notification-log"
          tabIndex={0}
          aria-label="Notifications, newest first"
        >
          {items.map((n) => {
            const mark = SEVERITY_MARK[n.severity];
            return (
              <li key={n.id} className="flex gap-3 px-3 py-2 text-sm" data-severity={n.severity}>
                <span className="num text-xs text-muted">{formatServerClock(n.at, zone.offsetAt)}</span>
                <span aria-hidden="true">{mark.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">
                    <span className="sr-only">{mark.label}: </span>
                    {n.title}
                  </p>
                  <p className="break-words text-muted">{n.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
